import axios from 'axios';

/**
 * Derive AI backend origin from REACT_APP_AZURE_AI_BACKEND_URL when it is the
 * full chat URL (FastAPI /api/v1/chat or legacy /api/chat).
 * Mirrors the pattern in AIScoring.js.
 */
function baseUrlFromAzureChatEnv() {
  const chat = process.env.REACT_APP_AZURE_AI_BACKEND_URL;
  if (!chat) return undefined;
  return chat
    .replace(/\/api\/v1\/chat\/?$/, '')
    .replace(/\/api\/chat\/?$/, '');
}

const AI_BACKEND_URL =
  process.env.REACT_APP_AI_BACKEND_URL ||
  baseUrlFromAzureChatEnv() ||
  'http://localhost:5000';

/**
 * Authenticated client for every ingest call (triggers, jobs and status). The AI backend checks
 * the Bearer token and scopes status results to the caller's organisation (ZVR-AI-0072).
 */
const getAuthClient = () => {
  const token = localStorage.getItem('jwtToken');
  return axios.create({
    baseURL: AI_BACKEND_URL,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    timeout: 30000,
  });
};

/** Status polls keep their shorter timeout. */
const STATUS_TIMEOUT_MS = 15000;

// ---------------------------------------------------------------------------
// Ingestion triggers (all require Bearer token)
// ---------------------------------------------------------------------------

/**
 * POST /api/v1/ingest/policy, single form
 * Enqueues ACS + Qdrant ingestion for one policy.
 *
 * @param {{
 *   policyId: string,
 *   fileId: string,
 *   versionNo?: string|null,
 *   modalSource?: string,
 *   docType?: string,      - 'policy' (default) or 'related_model' for IP Asset documents
 *   metadata?: object      - optional payload stored on Qdrant chunks (e.g. { related_model_type: 'trademark' })
 * }} params
 *
 * Response 202:
 *   { accepted: 1, items: [{ policyId, fileId, acs: { status }, qdrant: { status } }] }
 * Per-pipeline status values: "queued" | "in_progress" | "failed"
 */
export const ingestPolicy = ({
  policyId,
  fileId,
  versionNo = null,
  modalSource = null,
  docType = 'policy',
  metadata = undefined,
}) => {
  const client = getAuthClient();
  return client.post('/api/v1/ingest/policy', {
    policyId,
    fileId,
    versionNo,
    modalSource,
    docType,
    ...(metadata !== undefined && { metadata }),
  });
};

/**
 * POST /api/v1/ingest/policy, batch form (preferred for bulk upload)
 * Enqueues ingestion for up to 100 policies in a single HTTP call.
 *
 * Each item may include optional docType and metadata fields (see ingestPolicy).
 * @param {Array<{ policyId: string, fileId: string, modalSource?: string, docType?: string, metadata?: object }>} items
 *
 * Response 202:
 *   { accepted: N, items: [...] }
 */
export const ingestPolicyBatch = (items) => {
  const client = getAuthClient();
  return client.post('/api/v1/ingest/policy', { items });
};

/**
 * POST /api/v1/ingest/sync
 * Triggers a full ACS reindex + Qdrant catch-up (admin only).
 *
 * Response 202:
 *   { syncJobId, acs: { triggered, scope }, qdrant: { scheduled, scope } }
 * Response 409 (sync already in flight):
 *   { error: "a sync is already in flight", syncJobId: "<uuid-of-running-sync>" }
 */
export const triggerSync = () => {
  const client = getAuthClient();
  return client.post('/api/v1/ingest/sync');
};

// ---------------------------------------------------------------------------
// Status polling (Bearer token required; results are limited to the caller's organisation)
// ---------------------------------------------------------------------------

/**
 * GET /api/v1/ingest/status/{file_id}
 * Returns the latest ACS + Qdrant pipeline status for a single file.
 *
 * Response:
 *   {
 *     fileId, policyId, versionNo,
 *     acs:    { status, stage, indexed_at, completed_at, error, retries },
 *     qdrant: { status, ... }
 *   }
 * status values: "queued" | "running" | "completed" | "failed"
 */
export const getIngestStatus = (fileId) => {
  const client = getAuthClient();
  return client.get(`/api/v1/ingest/status/${encodeURIComponent(fileId)}`, { timeout: STATUS_TIMEOUT_MS });
};

/**
 * GET /api/v1/ingest/status?file_ids=uuid1,uuid2,...
 * Batch status for up to 100 file IDs.
 *
 * Response:
 *   { items: [ ...same shape as getIngestStatus... ] }
 */
export const getIngestStatusBatch = (fileIds) => {
  const client = getAuthClient();
  return client.get('/api/v1/ingest/status', {
    params: { file_ids: fileIds.join(',') },
    timeout: STATUS_TIMEOUT_MS,
  });
};

/**
 * GET /api/v1/ingest/jobs
 * Paginated listing of ingestion jobs from the AI backend DB.
 *
 * @param {object} opts
 * @param {string}  [opts.organizationId]
 * @param {string}  [opts.accountId]
 * @param {string}  [opts.docType]        - e.g. 'policy'
 * @param {string}  [opts.status]         - pending | processing | completed | failed
 * @param {number}  [opts.limit]          - default 50, max 200
 * @param {string}  [opts.cursor]         - opaque ISO cursor for next page
 *
 * Response shape:
 *   { items: [JobStatusResponse], next_cursor: string|null }
 *
 * JobStatusResponse key fields:
 *   job_id, file_id, filename, status, stage, doc_type, version_no,
 *   total_chunks, total_vectors_written, total_duration_ms,
 *   size_bytes, retry_count, error_message, error_stage,
 *   started_at, completed_at, created_at
 */
export const getIngestionJobs = ({
  organizationId,
  accountId,
  docType,
  status,
  limit,
  cursor,
} = {}) => {
  const client = getAuthClient();
  const params = {};
  if (organizationId) params.organization_id = organizationId;
  if (accountId) params.account_id = accountId;
  if (docType) params.doc_type = docType;
  if (status) params.status = status;
  if (limit) params.limit = limit;
  if (cursor) params.cursor = cursor;
  return client.get('/api/v1/ingest/jobs', { params });
};
